import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import cccApi, { FidelizadosDadosData, FidelizadosServicoItem, RecuperacaoMarcacao } from '../api/cccApi';
import {
  emptyServicoRow,
  FIDELIZADOS_WARNING_TEXT,
  isFidelizadosDadosIncomplete,
} from '../config/fidelizadosDados';
import { RecuperacaoMotivoWarningBanner } from './RecuperacaoMotivoWarning';

type Props = {
  marcacaoId: string;
  initial?: FidelizadosDadosData | null;
  disabled?: boolean;
  onSaved?: (marcacao: RecuperacaoMarcacao) => void;
};

const formatSavedAt = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso.replace('Z', '')).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const parseInitialServicos = (initial?: FidelizadosDadosData | null): FidelizadosServicoItem[] => {
  const rows = initial?.servicos || [];
  if (rows.length === 0) return [emptyServicoRow()];
  return rows.map((s) => ({
    nome: s.nome || '',
    quantidade: s.quantidade ?? null,
    valor_fatura: s.valor_fatura ?? null,
  }));
};

const RecuperacaoFidelizadosForm: React.FC<Props> = ({ marcacaoId, initial, disabled, onSaved }) => {
  const [fornecedorAtual, setFornecedorAtual] = useState(initial?.fornecedor_atual || '');
  const [dataInicioContrato, setDataInicioContrato] = useState(initial?.data_inicio_contrato || '');
  const [servicos, setServicos] = useState<FidelizadosServicoItem[]>(parseInitialServicos(initial));
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState<{
    updated_at?: string | null;
    updated_by_name?: string | null;
  }>({
    updated_at: initial?.updated_at,
    updated_by_name: initial?.updated_by_name,
  });

  useEffect(() => {
    setFornecedorAtual(initial?.fornecedor_atual || '');
    setDataInicioContrato(initial?.data_inicio_contrato || '');
    setServicos(parseInitialServicos(initial));
    setMeta({
      updated_at: initial?.updated_at,
      updated_by_name: initial?.updated_by_name,
    });
  }, [
    marcacaoId,
    initial?.fornecedor_atual,
    initial?.data_inicio_contrato,
    initial?.servicos,
    initial?.updated_at,
    initial?.updated_by_name,
  ]);

  const updateServico = (index: number, patch: Partial<FidelizadosServicoItem>) => {
    setServicos((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addServico = () => {
    setServicos((prev) => [...prev, emptyServicoRow()]);
  };

  const removeServico = (index: number) => {
    setServicos((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyServicoRow()];
    });
  };

  const buildPayload = () => ({
    fornecedor_atual: fornecedorAtual.trim() || null,
    data_inicio_contrato: dataInicioContrato.trim() || null,
    servicos: servicos.map((row) => ({
      nome: (row.nome || '').trim() || null,
      quantidade:
        row.quantidade === null || row.quantidade === undefined || row.quantidade === ''
          ? null
          : Number(row.quantidade),
      valor_fatura:
        row.valor_fatura === null || row.valor_fatura === undefined || row.valor_fatura === ''
          ? null
          : Number(row.valor_fatura),
    })),
  });

  const handleSave = async () => {
    if (disabled || saving) return;
    setSaving(true);
    try {
      const res = await cccApi.updateFidelizadosDados(marcacaoId, buildPayload());
      if (!res.success || !res.marcacao) {
        message.error(res.message || 'Não foi possível guardar o formulário');
        return;
      }
      const saved = res.marcacao.recuperacao_claim?.fidelizados_dados;
      if (saved) {
        setMeta({
          updated_at: saved.updated_at,
          updated_by_name: saved.updated_by_name,
        });
      }
      onSaved?.(res.marcacao);
      message.success('Formulário guardado');
    } finally {
      setSaving(false);
    }
  };

  const formIncomplete = isFidelizadosDadosIncomplete({
    fornecedor_atual: fornecedorAtual.trim() || null,
    data_inicio_contrato: dataInicioContrato.trim() || null,
    servicos: servicos.map((row) => ({
      nome: row.nome,
      quantidade: row.quantidade,
      valor_fatura: row.valor_fatura,
    })),
  });

  const savedLabel = formatSavedAt(meta.updated_at);

  return (
    <div className="rec-fidelizados-form">
      {formIncomplete && <RecuperacaoMotivoWarningBanner message={FIDELIZADOS_WARNING_TEXT} />}

      <div className="rec-fidelizados-fields">
        <label className="rec-fidelizados-field">
          <span className="rec-fidelizados-label">
            Fornecedor Atual <span className="rec-required-asterisk">*</span>
          </span>
          <input
            type="text"
            className="rec-fidelizados-input"
            value={fornecedorAtual}
            disabled={disabled}
            placeholder="Nome do fornecedor atual do cliente"
            onChange={(e) => setFornecedorAtual(e.target.value)}
          />
        </label>

        <label className="rec-fidelizados-field">
          <span className="rec-fidelizados-label">Data de Fim do Contrato</span>
          <input
            type="date"
            className="rec-fidelizados-input"
            value={dataInicioContrato}
            disabled={disabled}
            onChange={(e) => setDataInicioContrato(e.target.value)}
          />
        </label>
      </div>

      <section className="rec-fidelizados-servicos">
        <div className="rec-fidelizados-servicos-head">
          <h4 className="rec-fidelizados-servicos-title">Serviços</h4>
          <button
            type="button"
            className="rec-btn rec-btn-secondary rec-btn-sm"
            disabled={disabled}
            onClick={addServico}
          >
            + Adicionar serviço
          </button>
        </div>

        <div className="rec-fidelizados-servicos-table-wrap">
          <table className="rec-fidelizados-servicos-table">
            <thead>
              <tr>
                <th>Serviço</th>
                <th className="rec-fidelizados-col-qty">Quantidade</th>
                <th className="rec-fidelizados-col-valor">Valor da fatura (€)</th>
                <th className="rec-fidelizados-servicos-actions-col" aria-hidden />
              </tr>
            </thead>
            <tbody>
              {servicos.map((row, index) => (
                <tr key={`servico-${index}`}>
                  <td>
                    <input
                      type="text"
                      className="rec-fidelizados-input"
                      value={row.nome || ''}
                      disabled={disabled}
                      placeholder="Ex.: Eletricidade"
                      onChange={(e) => updateServico(index, { nome: e.target.value })}
                    />
                  </td>
                  <td className="rec-fidelizados-col-qty">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="rec-fidelizados-input rec-fidelizados-input-qty"
                      value={row.quantidade ?? ''}
                      disabled={disabled}
                      placeholder="—"
                      onChange={(e) =>
                        updateServico(index, {
                          quantidade: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                    />
                  </td>
                  <td className="rec-fidelizados-col-valor">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="rec-fidelizados-input rec-fidelizados-input-valor"
                      value={row.valor_fatura ?? ''}
                      disabled={disabled}
                      placeholder="Aproximadamente"
                      onChange={(e) => {
                        const raw = e.target.value.replace(',', '.');
                        updateServico(index, {
                          valor_fatura: raw === '' ? null : Number(raw),
                        });
                      }}
                    />
                  </td>
                  <td className="rec-fidelizados-servicos-actions-col">
                    <button
                      type="button"
                      className="rec-fidelizados-remove"
                      disabled={disabled || servicos.length <= 1}
                      title="Remover linha"
                      onClick={() => removeServico(index)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="rec-fidelizados-actions">
        <button
          type="button"
          className="rec-btn rec-btn-primary"
          disabled={disabled || saving}
          onClick={handleSave}
        >
          {saving ? 'A guardar…' : 'Guardar Feedback'}
        </button>
      </div>

      {savedLabel && (
        <p className="rec-motivo-meta">
          Última atualização{meta.updated_by_name ? ` por ${meta.updated_by_name}` : ''}: {savedLabel}
        </p>
      )}
    </div>
  );
};

export default RecuperacaoFidelizadosForm;
